import React, { useEffect, useState } from "react";
import { Trash2, Plus, Minus, PlusCircle, RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";
import libFloat from "../../../../../util/libFloat";
import {
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Paper,
	TableContainer,
} from "@mui/material";
import AddProductFlow from "./add-product-flow";

// Input de quantidade editável por digitação — útil pra quem vai lançar muitas unidades de
// uma vez (ex: 20 camisetas G) sem precisar clicar em "+" vinte vezes. Mantém um buffer local
// de texto pra permitir apagar/digitar livremente; só confirma (e recalcula o valor) no blur
// ou Enter, e nunca aceita menos que 1 — reverte pro valor anterior se digitarem algo inválido.
function GradeQtyInput({ value, onCommit }) {
	const [texto, setTexto] = useState(String(value));

	useEffect(() => {
		setTexto(String(value));
	}, [value]);

	const commit = () => {
		const n = parseInt(texto, 10);
		if (!isNaN(n) && n >= 1) {
			if (n !== value) onCommit(n);
			setTexto(String(n));
		} else {
			setTexto(String(value));
		}
	};

	return (
		<input
			type="text"
			inputMode="numeric"
			pattern="[0-9]*"
			value={texto}
			onChange={(e) => setTexto(e.target.value.replace(/[^0-9]/g, ""))}
			onFocus={(e) => e.target.select()}
			onBlur={commit}
			onKeyDown={(e) => {
				if (e.key === "Enter") {
					e.preventDefault();
					commit();
					e.target.blur();
				}
			}}
			className="h-6 w-10 rounded-full bg-gray-50 text-center text-xs font-bold tabular-nums text-gray-900 transition-all focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
		/>
	);
}

export default function StepProdutos({
	itens,
	setItens,
	complementos = [],
	setComplementos,
	totalVenda,
	setTotalVenda,
	onAddingProductChange,
	originalItens = [],
	originalComplementos = [],
}) {
	const [addingProduct, setAddingProduct] = useState(false);
	const formatCurrency = (val) => libFloat.strToCurrency(val || 0);

	// Notifica o modal pai quando o estado de adição interna mudar
	useEffect(() => {
		if (onAddingProductChange) {
			onAddingProductChange(addingProduct);
		}
	}, [addingProduct, onAddingProductChange]);

	// Total da linha = valor próprio do item + soma dos complementos dele, seguindo a regra
	// do sz-foods (REGRAS-GRADE.md / product-rules.js): Grade zera o pai e o valor vai todo
	// pro complemento (0 + valor_grade); Subitem zera o complemento e consolida no pai
	// (valor_pai + 0); Normal com Adicional soma os dois (base + adicional). É a mesma soma
	// nos três casos — nunca precisa saber o tipo aqui, só somar.
	const getComplementosDoItem = (item) => (complementos || []).filter((c) => c.id_venda_item === item.id_venda_item);
	const calcularTotalLinha = (item) => {
		const valorProprio = Number(item.valor_total ?? item.valortotal ?? item.valor ?? 0);
		const qtdeItem = Number(item.qtde ?? item.quantidade ?? 1);
		const valorComplementos = getComplementosDoItem(item)
			.filter((c) => !c._removido)
			.reduce(
				(acc, c) => acc + Number(c.valor ?? c.valortotal ?? 0) * qtdeItem,
				0,
			);
		return valorProprio + valorComplementos;
	};

	// Recalcula o total EXCLUINDO itens marcados como removidos — o soft delete
	// não tira da lista, mas itens com _removido não entram na conta.
	useEffect(() => {
		const novoTotal = (itens || [])
			.filter((item) => !item._removido)
			.reduce((acc, item) => acc + calcularTotalLinha(item), 0);
		setTotalVenda(novoTotal);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [itens, complementos, setTotalVenda]);

	// Conta quantos itens estão marcados pra exclusão — pra exibir no banner
	const itensRemovidos = (itens || []).filter((i) => i._removido).length;

	// Soft Delete de um complemento específico (útil para variações de grade individuais)
	// Restaura a quantidade e o valor originais do banco no momento do delete para o faturamento ficar correto.
	// Se for o último complemento ativo do item, marca o item pai como removido também.
	const handleRemoverComplemento = (globalCompIdx) => {
		const comp = complementos[globalCompIdx];
		if (!comp) return;

		setComplementos((prev) => {
			const novosComps = prev.map((c, i) => {
				if (i !== globalCompIdx) return c;
				// Procura no array de complementos originais
				const orig = (originalComplementos || []).find(
					(oc) => oc.id_complementos_item === c.id_complementos_item && oc.id_venda_item === c.id_venda_item
				);
				const qtdeOriginal = orig ? Number(orig.qtde ?? orig.quantidade ?? 1) : 1;
				const valorUnit = Number(c.valorunit || c.valor_unit || c.valor_item || 0);

				return {
					...c,
					qtde: qtdeOriginal,
					quantidade: qtdeOriginal,
					valor: valorUnit * qtdeOriginal,
					valortotal: valorUnit * qtdeOriginal,
					_qtde_alterada: false, // reseta modificações
					_removido: true
				};
			});

			// Verifica se sobrou algum complemento ativo para este id_venda_item
			const temAtivo = novosComps.some(
				(c) => c.id_venda_item === comp.id_venda_item && !c._removido
			);

			if (!temAtivo) {
				// Marca o pai correspondente como removido
				setItens((prevItens) =>
					prevItens.map((it) => {
						if (it.id_venda_item !== comp.id_venda_item) return it;
						const origPai = (originalItens || []).find((oi) => oi.id_venda_item === it.id_venda_item);
						const qtdePaiOrig = origPai ? Number(origPai.qtde ?? origPai.quantidade ?? 1) : 1;
						const valorPaiUnit = Number(it.valor_unitario ?? it.valorunitario ?? it.valor_unit ?? 0);
						return {
							...it,
							qtde: qtdePaiOrig,
							quantidade: qtdePaiOrig,
							valor: valorPaiUnit * qtdePaiOrig,
							valor_total: valorPaiUnit * qtdePaiOrig,
							_removido: true
						};
					})
				);
			}

			return novosComps;
		});
	};

	// Desfaz soft delete de um complemento específico.
	// Garante que o item pai correspondente também seja restaurado caso estivesse marcado como removido.
	const handleRestaurarComplemento = (globalCompIdx) => {
		const comp = complementos[globalCompIdx];
		if (!comp) return;

		setComplementos((prev) => {
			const novosComps = prev.map((c, i) => {
				if (i !== globalCompIdx) return c;
				const { _removido, ...rest } = c;
				return rest;
			});

			// Restaura o pai correspondente
			setItens((prevItens) =>
				prevItens.map((it) => {
					if (it.id_venda_item !== comp.id_venda_item) return it;
					const { _removido, ...rest } = it;
					return rest;
				})
			);

			return novosComps;
		});
	};

	// Item novo (_novo: true) nunca existiu no banco — não tem o que marcar pra exclusão,
	// só tira do array de vez. Item existente usa soft delete (_removido = true) pra poder
	// mandar ao backend quem sobreviveu e quem foi marcado pra exclusão.
	const handleRemoverItem = (index, idVendaItem) => {
		const item = itens[index];

		if (item?._novo) {
			setItens((prev) => prev.filter((_, i) => i !== index));
			if (idVendaItem) {
				setComplementos((prev) => prev.filter((c) => c.id_venda_item !== idVendaItem));
			}
			return;
		}

		// Reverte o item pai para sua quantidade original do banco
		setItens((prev) =>
			prev.map((it, i) => {
				if (i !== index) return it;
				const orig = (originalItens || []).find((oi) => oi.id_venda_item === it.id_venda_item);
				const qtdeOriginal = orig ? Number(orig.qtde ?? orig.quantidade ?? 1) : 1;
				const valorUnit = Number(it.valor_unitario ?? it.valorunitario ?? it.valor_unit ?? 0);
				return {
					...it,
					qtde: qtdeOriginal,
					quantidade: qtdeOriginal,
					valor: valorUnit * qtdeOriginal,
					valor_total: valorUnit * qtdeOriginal,
					_removido: true,
				};
			})
		);

		// Reverte todos os complementos do item para suas respectivas quantidades originais
		if (idVendaItem) {
			setComplementos((prev) =>
				prev.map((c) => {
					if (c.id_venda_item !== idVendaItem) return c;
					const orig = (originalComplementos || []).find(
						(oc) => oc.id_complementos_item === c.id_complementos_item && oc.id_venda_item === c.id_venda_item
					);
					const qtdeOriginal = orig ? Number(orig.qtde ?? orig.quantidade ?? 1) : 1;
					const valorUnit = Number(c.valorunit || c.valor_unit || c.valor_item || 0);
					return {
						...c,
						qtde: qtdeOriginal,
						quantidade: qtdeOriginal,
						valor: valorUnit * qtdeOriginal,
						valortotal: valorUnit * qtdeOriginal,
						_qtde_alterada: false,
						_removido: true,
					};
				})
			);
		}
	};

	// Desfaz o soft delete — restaura o item e seus complementos
	const handleRestaurarItem = (index, idVendaItem) => {
		setItens((prev) =>
			prev.map((item, i) => {
				if (i !== index) return item;
				const { _removido, ...rest } = item;
				return rest;
			}),
		);
		if (idVendaItem) {
			setComplementos((prev) =>
				prev.map((c) => {
					if (c.id_venda_item !== idVendaItem) return c;
					const { _removido, ...rest } = c;
					return rest;
				}),
			);
		}
	};

	// Altera a quantidade de um complemento de grade — recalcula o valor total
	// proporcionalmente (valor = valorUnit * novaQtde). Mínimo 1 pra não zerar a grade.
	const definirQtdeComplemento = (compIndex, novaQtdeBruta) => {
		setComplementos((prev) =>
			prev.map((c, i) => {
				if (i !== compIndex) return c;
				const novaQtde = Math.max(1, Math.trunc(Number(novaQtdeBruta) || 1));
				const valorUnit = Number(c.valorunit || c.valor_unit || c.valor_item || 0);
				return {
					...c,
					qtde: novaQtde,
					quantidade: novaQtde,
					valor: valorUnit * novaQtde,
					valortotal: valorUnit * novaQtde,
					_qtde_alterada: true,
				};
			}),
		);
	};

	// Botões +/-: aplicam um delta em cima da quantidade atual
	const handleAlterarQtdeComplemento = (compIndex, delta) => {
		const atual = Number(complementos[compIndex]?.qtde ?? complementos[compIndex]?.quantidade ?? 1);
		definirQtdeComplemento(compIndex, atual + delta);
	};

	// Recebe o item+complementos já prontos do AddProductFlow e anexa nas listas dessa etapa
	const handleAdicionarProduto = (novoItem, novosComplementos) => {
		setItens((prev) => [...prev, novoItem]);
		if (novosComplementos.length > 0) {
			setComplementos((prev) => [...prev, ...novosComplementos]);
		}
		setAddingProduct(false);
	};

	if (addingProduct) {
		return (
			<div className="h-full min-h-[420px]">
				<AddProductFlow onAdd={handleAdicionarProduto} onCancel={() => setAddingProduct(false)} />
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Banner Informativo */}
			<div className="bg-blue-50/50 border border-blue-100 p-4 rounded-2xl flex justify-between items-center">
				<div>
					<h3 className="text-sm font-bold text-blue-800 uppercase tracking-wider mb-1">Passo 1: Alterar Produtos</h3>
					<p className="text-xs text-blue-600">Ajuste as quantidades, valores ou remova itens da venda.</p>
				</div>
				<div className="flex items-center gap-4">
					<button
						type="button"
						onClick={() => setAddingProduct(true)}
						className="flex items-center gap-1.5 rounded-xl bg-blue-600 px-4 py-2 text-xs font-bold text-white shadow-sm transition-all hover:bg-blue-700"
					>
						<PlusCircle size={16} /> Adicionar Produto
					</button>
					<div className="text-right">
						<span className="text-[10px] uppercase font-bold text-gray-400 block">Total Provisório</span>
						<span className="text-xl font-black text-blue-600">{formatCurrency(totalVenda)}</span>
					</div>
				</div>
			</div>

			{/* Indicador de itens removidos */}
			{itensRemovidos > 0 && (
				<div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50/50 px-4 py-2.5">
					<div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
					<span className="text-xs font-bold text-red-700">
						{itensRemovidos} {itensRemovidos === 1 ? "item marcado" : "itens marcados"} para exclusão
					</span>
					<span className="text-[10px] text-red-500 font-medium">
						— serão removidos ao salvar
					</span>
				</div>
			)}

			{/* Tabela de Produtos */}
			<TableContainer component={Paper} className="shadow-none border border-gray-150 rounded-2xl overflow-hidden">
				<Table size="small">
					<TableHead className="color-table">
						<TableRow>
							<TableCell style={{ color: "#fff", fontWeight: "bold", width: "5%" }}>#</TableCell>
							<TableCell style={{ color: "#fff", fontWeight: "bold", width: "40%" }}>Produto / Complemento</TableCell>
							<TableCell align="center" style={{ color: "#fff", fontWeight: "bold", width: "15%" }}>Qtde</TableCell>
							<TableCell align="right" style={{ color: "#fff", fontWeight: "bold", width: "15%" }}>Valor Unit.</TableCell>
							<TableCell align="right" style={{ color: "#fff", fontWeight: "bold", width: "15%" }}>Total</TableCell>
							<TableCell align="center" style={{ color: "#fff", fontWeight: "bold", width: "5%" }}></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{itens?.length > 0 ? (
							itens.map((item, index) => {
								const itemComps = (complementos || []).filter((c) => c.id_venda_item === item.id_venda_item);
								const hasComps = itemComps.length > 0;
								// Identifica se é Grade verificando o TIPO do complemento ou se o pai tem valor menor/igual a zero (fallback)
								const hasGradeComp = hasComps && (
									itemComps.some((c) => c.tipo?.toUpperCase() === "GRADE" || c.TIPO?.toUpperCase() === "GRADE") ||
									Number(item.valor_total ?? item.valortotal ?? item.valor ?? 0) <= 0
								);
								const qtde = Number(item.qtde ?? item.quantidade ?? 1);
								const isRemovido = !!item._removido;

								return (
									<React.Fragment key={index}>
										{/* Linha do Produto — sempre fundo azul (identifica venda_item) */}
										<TableRow
											hover={!isRemovido}
											style={{
								backgroundColor: "#eff6ff",
												opacity: isRemovido ? 0.6 : 1,
												transition: "all 0.3s ease",
											}}
										>
											<TableCell className="font-extrabold text-gray-700">
												<span style={{ textDecoration: isRemovido ? "line-through" : "none" }}>
													{index + 1}
												</span>
											</TableCell>
											<TableCell style={{
												fontWeight: "bold",
												color: isRemovido ? "#dc2626" : "#1e3a8a",
											}}>
												<span className="inline-flex items-center gap-2" style={{ textDecoration: isRemovido ? "line-through" : "none" }}>
													{item.nome || item.descricao || "—"}
													{item._novo && !isRemovido && (
														<span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-emerald-700">
															Novo
														</span>
													)}
													{isRemovido && (
														<span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-red-600" style={{ textDecoration: "none" }}>
															Excluído
														</span>
													)}
												</span>
											</TableCell>

											{/* Quantidade — valor cru do item; em branco quando é grade */}
											<TableCell align="center" style={{ fontWeight: "bold", textDecoration: isRemovido ? "line-through" : "none", color: isRemovido ? "#dc2626" : "#1e3a8a" }}>
												{hasGradeComp ? "" : hasComps ? `${qtde}x` : qtde}
											</TableCell>

											{/* Valor Unitário — valor cru do item; em branco quando é grade */}
											<TableCell align="right" style={{ fontWeight: "bold", textDecoration: isRemovido ? "line-through" : "none", color: isRemovido ? "#dc2626" : "#1e3a8a" }}>
												{hasGradeComp ? "" : formatCurrency(item.valor_unitario ?? item.valorunitario ?? item.valor_unit ?? 0)}
											</TableCell>

											{/* Total: valor cru do item; em branco quando é grade (o total de verdade vai na linha "Total {produto}") */}
											<TableCell align="right" style={{ fontWeight: "bold", textDecoration: isRemovido ? "line-through" : "none", color: isRemovido ? "#dc2626" : "#1e3a8a" }}>
												{hasGradeComp ? "" : formatCurrency(item.valor_total ?? item.valortotal ?? item.valor ?? 0)}
											</TableCell>

											{/* Ação: Remover ou Restaurar */}
											<TableCell align="center">
												{isRemovido ? (
													<button
														onClick={() => handleRestaurarItem(index, item.id_venda_item)}
														className="text-blue-500 hover:text-blue-700 transition-colors p-1 rounded-lg hover:bg-blue-50"
														title="Desfazer exclusão"
													>
														<RotateCcw size={16} />
													</button>
												) : (
													<button
														onClick={() => handleRemoverItem(index, item.id_venda_item)}
														className="text-red-500 hover:text-red-700 transition-colors p-1 rounded-lg hover:bg-red-50"
														title="Marcar para exclusão"
													>
														<Trash2 size={16} />
													</button>
												)}
											</TableCell>
										</TableRow>

										{/* Linhas de Complementos */}
										{itemComps.map((comp, compIdx) => {
											// `tipo`/`is_grade` vêm inconsistentes/vazios em registros reais — usa o mesmo
											// sinal confiável do pai (hasGradeComp, valor zerado) como detecção principal.
											const isGrade = hasGradeComp || String(comp.tipo || "").toUpperCase() === "GRADE" || comp.is_grade || comp.IS_GRADE;
											const compQtde = Number(comp.qtde ?? comp.quantidade ?? 1);
											// Índice real do complemento no array global (não o local filtrado)
											const globalCompIdx = (complementos || []).indexOf(comp);

											const isCompRemovido = !!comp._removido;

											return (
											<TableRow
												key={`comp-${compIdx}`}
												hover={!isRemovido && !isCompRemovido}
												style={{
													opacity: isRemovido || isCompRemovido ? 0.4 : 1,
													backgroundColor: isRemovido || isCompRemovido ? "#fef2f2" : undefined,
													transition: "all 0.3s ease",
												}}
											>
												<TableCell></TableCell>
												<TableCell style={{
													paddingLeft: "30px",
													color: isRemovido || isCompRemovido ? "#dc2626" : "#4b5563",
													textDecoration: isRemovido || isCompRemovido ? "line-through" : "none",
												}}>
													<span className="inline-flex items-center gap-2">
														{comp.nome || comp.descricao || "—"}
														{isGrade && (
															<span className="rounded-md bg-blue-100 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-blue-700">
																Grade
															</span>
														)}
														{isCompRemovido && !isRemovido && (
															<span className="rounded-full bg-red-100 px-1.5 py-0.2 text-[8px] font-black uppercase tracking-wide text-red-600 no-underline" style={{ textDecoration: "none" }}>
																Removido
															</span>
														)}
													</span>
												</TableCell>

												{/* Quantidade — com controles +/- se for grade e não estiver removido */}
												<TableCell align="center" style={{ color: isRemovido || isCompRemovido ? "#dc2626" : "#4b5563", textDecoration: isRemovido || isCompRemovido ? "line-through" : "none" }}>
													{isGrade && !isRemovido && !isCompRemovido ? (
														<div className="inline-flex items-center gap-0.5 rounded-full border border-gray-200 bg-white p-0.5 shadow-sm">
															<button
																type="button"
																onClick={() => handleAlterarQtdeComplemento(globalCompIdx, -1)}
																disabled={compQtde <= 1}
																className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-white transition-all hover:bg-blue-700 active:scale-95 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
															>
																<ChevronLeft size={12} />
															</button>
															<GradeQtyInput
																value={compQtde}
																onCommit={(novaQtde) => definirQtdeComplemento(globalCompIdx, novaQtde)}
															/>
															<button
																type="button"
																onClick={() => handleAlterarQtdeComplemento(globalCompIdx, +1)}
																className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-white transition-all hover:bg-blue-700 active:scale-95"
															>
																<ChevronRight size={12} />
															</button>
														</div>
													) : (
														compQtde
													)}
												</TableCell>

												<TableCell align="right" style={{ color: isRemovido ? "#dc2626" : "#4b5563", textDecoration: isRemovido ? "line-through" : "none" }}>
													{!isRemovido && qtde > 1 && (
														<span className="mr-1 text-[10px] font-bold text-gray-400">{qtde} ×</span>
													)}
													{formatCurrency(comp.valorunit || comp.valor_unit || comp.valor || 0)}
												</TableCell>
												<TableCell align="right" style={{ color: comp._removido || isRemovido ? "#dc2626" : "#4b5563", textDecoration: comp._removido || isRemovido ? "line-through" : "none" }}>
													{formatCurrency(Number(comp.valor || comp.valortotal || 0) * qtde)}
												</TableCell>
												<TableCell align="center">
													{!isRemovido && isGrade && (
														comp._removido ? (
															<button
																onClick={() => handleRestaurarComplemento(globalCompIdx)}
																className="text-blue-500 hover:text-blue-700 transition-colors p-1 rounded-lg hover:bg-blue-50"
																title="Desfazer exclusão do complemento"
															>
																<RotateCcw size={14} />
															</button>
														) : (
															<button
																onClick={() => handleRemoverComplemento(globalCompIdx)}
																className="text-red-500 hover:text-red-700 transition-colors p-1 rounded-lg hover:bg-red-50"
																title="Remover complemento"
															>
																<Trash2 size={14} />
															</button>
														)
													)}
												</TableCell>
											</TableRow>
											);
										})}

										{/* Linha do Desconto do Item — se houver desconto cadastrado no item pai */}
										{Number(item.desconto ?? item.DESCONTO ?? 0) > 0 && (
											<TableRow hover style={{ opacity: isRemovido ? 0.5 : 1 }}>
												<TableCell></TableCell>
												<TableCell style={{ paddingLeft: "30px", color: "#dc2626" }}>
													(-) DESCONTO
												</TableCell>
												<TableCell align="center" style={{ color: "#dc2626" }}></TableCell>
												<TableCell align="right" style={{ color: "#dc2626" }}></TableCell>
												<TableCell align="right" style={{ color: "#dc2626" }}>
													-{formatCurrency(item.desconto ?? item.DESCONTO ?? 0)}
												</TableCell>
												<TableCell></TableCell>
											</TableRow>
										)}

										{/* Total do produto: aparece quando tem complemento ou desconto */}
										{(hasComps || Number(item.desconto ?? item.DESCONTO ?? 0) > 0) && (
											<TableRow style={{ opacity: isRemovido ? 0.5 : 1 }}>
												<TableCell></TableCell>
												<TableCell colSpan={3} align="right" className="text-xs font-black uppercase text-blue-800">
													Total
												</TableCell>
												<TableCell align="right" className="font-black text-blue-800">
													{formatCurrency(calcularTotalLinha(item))}
												</TableCell>
												<TableCell></TableCell>
											</TableRow>
										)}
									</React.Fragment>
								);
							})
						) : (
							<TableRow>
								<TableCell colSpan={6} align="center" style={{ padding: "30px", color: "#6b7280" }}>
									Nenhum produto na lista de alteração.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</div>
	);
}













